import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { useSEO, SchemaOrg } from '@/components/base/SEO';
import { categories, mangaList } from '@/mocks/manga';

const SITE_URL = 'https://beilemanga.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const filteredManga = useMemo(
    () => (slug ? mangaList.filter((m) => m.category === slug) : []),
    [slug]
  );
  const canonicalUrl = category ? `${SITE_URL}/category/${slug}` : SITE_URL;

  useSEO({
    title: category ? `${category.name} - 贝勒漫画 | ${category.description}` : '分类 - 贝勒漫画',
    description: category ? `${category.description}，贝勒漫画网为您精选优质${category.name}资源，每日更新，高清在线观看。` : '贝勒漫画网漫画分类页面',
    keywords: category ? `${category.name}, 贝勒漫画, ${category.name}在线观看, ${category.name}推荐, 漫画在线阅读` : '贝勒漫画, 漫画分类',
    canonicalUrl,
    ogType: 'website',
  });

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: category.name, item: canonicalUrl },
    ],
  };

  return (
    <>
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: category.name }]} />

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
              <i className={`${category.icon} text-2xl w-8 h-8 flex items-center justify-center`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-md mx-auto">{category.description}</p>
          </div>

          {/* Subcategory Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto flex-wrap justify-center">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredManga.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredManga.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-600">暂无该分类的漫画，敬请期待</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}